import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { Languages } from 'lucide-react'
import { cn } from '@/lib/utils'
import { localeNames, siteLocales, toLocale } from '@/i18n/config'
import { common } from '@/i18n/common'

interface LanguageSwitchProps {
    tone?: 'light' | 'dark'
    className?: string
}

export const LanguageSwitch: FC<LanguageSwitchProps> = ({ tone = 'light', className }): ReactNode => {
    const router = useRouter()
    const locale = toLocale(router.locale)

    return (
        <div
            className={cn('inline-flex items-center gap-0.5 rounded-full border p-0.5 text-xs font-medium', tone === 'dark' ? 'border-white/15' : 'border-border', className)}
            role='group'
            aria-label={common[locale].nav.language}
        >
            <Languages className={cn('mx-1.5 size-3.5', tone === 'dark' ? 'text-white/60' : 'text-muted-foreground')} aria-hidden='true' />

            {siteLocales.map(item => (
                <Link
                    key={item}
                    href={router.asPath}
                    locale={item}
                    scroll={false}
                    hrefLang={localeNames[item].hreflang}
                    aria-current={item === locale ? 'true' : undefined}
                    aria-label={localeNames[item].name}
                    className={cn(
                        'rounded-full px-2.5 py-1 transition-colors',
                        item === locale
                            ? tone === 'dark' ? 'bg-white text-ink' : 'bg-foreground text-background'
                            : tone === 'dark' ? 'text-white/70 hover:text-white' : 'text-muted-foreground hover:text-foreground'
                    )}
                >
                    {localeNames[item].short}
                </Link>
            ))}
        </div>
    )
}
